<script setup lang="ts">
import { computed } from 'vue';
import { useRecipeImageUrl } from '@shared/app/composables/useRecipeImageUrl';
import { simpleFormatAllowedDecorate } from '@shared/app/utils/simpleFormatAllowedDecorate';
import type { RecipeDetailResponse } from '~~/server/api/kiwi_user/recipes/detail';

const props = withDefaults(
  defineProps<{
    recipeData?: RecipeDetailResponse | null;
    showFavoriteButton?: boolean;
  }>(),
  {
    recipeData: null,
    showFavoriteButton: true,
  },
);

const { createRecipeMainImageUrl } = useRecipeImageUrl();

const mainImageUrl = computed(() => {
  if (props.recipeData?.main_image) {
    return createRecipeMainImageUrl({
      recipeId: props.recipeData.id,
      filename: props.recipeData.main_image,
      w: 375,
      h: 300,
    });
  }
  return undefined;
});

const leadHtml = computed(() => {
  if (!props.recipeData?.lead) {
    return '';
  }

  return simpleFormatAllowedDecorate(props.recipeData.lead);
});

const pointHtml = computed(() => {
  if (!props.recipeData?.point) {
    return '';
  }

  return simpleFormatAllowedDecorate(props.recipeData.point);
});
</script>

<template>
  <div>
    <h1 class="text-2xl font-bold text-gray-500 mb-5">
      {{ recipeData?.title }}
    </h1>

    <div class="flex max-md:flex-col gap-5 mb-8">
      <div class="md:w-[45%] shrink-0">
        <div class="mb-3 relative">
          <img v-if="mainImageUrl" :src="mainImageUrl" />
          <AtomsPlaceholderImage
            v-else
            class="w-full object-cover aspect-[375/300] bg-white"
          />
          <div v-if="showFavoriteButton" class="absolute bottom-1.5 right-1.5">
            <OrganismsFavoriteButton
              v-if="recipeData?.id"
              :recipe-id="recipeData.id"
            />
          </div>
        </div>

        <AtomsRecipeInfoStats
          :energy="recipeData?.energy"
          :cooking-time="recipeData?.cooking_time"
          :estimated-cost="recipeData?.estimated_cost"
        />

        <AtomsPickedTagsSection
          v-if="recipeData?.associated_picked_tags"
          class="mt-5"
          :tags="recipeData.associated_picked_tags"
          title="このレシピの特徴"
        />
      </div>

      <div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="sanitized-html text-lg text-gray-500 mb-3 items-center"
          v-html="leadHtml"
        />
        <!-- eslint-enable vue/no-v-html -->

        <AtomsAuthorSmallSection
          v-if="recipeData?.author"
          class="mb-5"
          :author="recipeData.author"
        />

        <div>
          <h2 class="mb-1.5">
            <span class="text-brown-300 text-lg font-bold">材料</span>
            <span class="text-gray-500"
              >（{{ recipeData?.quantity }}人分）</span
            >
          </h2>

          <ul>
            <li
              v-for="(composition, i) in recipeData?.composition_group_default
                ?.recipe_compositions"
              :key="i"
              class="flex justify-between gap-2 flex-wrap text-gray-500 border-t border-cream-500 py-1.5"
            >
              <span>{{ composition.title }}</span>
              <span>{{ composition.quantity }}</span>
            </li>
          </ul>
        </div>

        <div
          v-for="(group, i) in recipeData?.composition_group_others"
          :key="i"
          class="mt-3"
        >
          <h2 class="mb-1.5">
            <span class="text-brown-300 text-lg font-bold">{{
              group.title
            }}</span>
          </h2>

          <ul>
            <li
              v-for="(composition, i2) in group.recipe_compositions"
              :key="i2"
              class="flex justify-between gap-2 flex-wrap text-gray-500 border-t border-cream-500 py-1.5"
            >
              <span>{{ composition.title }}</span>
              <span>{{ composition.quantity }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <AtomsContraindicationFoodSection
      v-if="recipeData?.contraindication_food_groups"
      class="my-10"
      :groups="recipeData.contraindication_food_groups"
    />

    <div v-if="recipeData?.recipe_steps.length" class="my-10">
      <h2 class="text-brown-300 text-xl font-bold mb-3">作り方</h2>
      <ul>
        <ol
          v-for="(step, i) in recipeData?.recipe_steps"
          :key="i"
          class="border-t border-cream-500 py-2 flex"
        >
          <p class="text-pink-400 shrink-0 font-bold px-3">{{ i + 1 }}</p>
          <p class="text-gray-500 whitespace-pre-wrap">{{ step.title }}</p>
        </ol>
      </ul>
    </div>

    <div v-if="pointHtml" class="my-10">
      <h2 class="text-brown-300 text-xl font-bold mb-3">
        管理栄養士からのコツ・ポイント
      </h2>

      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="sanitized-html text-gray-500" v-html="pointHtml" />
    </div>

    <AtomsAdPoets ad-spot-id="NDY5OjY5Ng" />

    <AtomsNutrientTable
      v-if="recipeData?.nutrients"
      class="my-10"
      :nutrients="recipeData.nutrients"
    />

    <AtomsAuthorSection
      v-if="recipeData?.author"
      class="mb-10"
      :author="recipeData.author"
    />

    <AtomsShareSection />
  </div>
</template>

<style scoped>
@reference '~/assets/css/main.css';
:deep(.sanitized-html p:not(:last-child)) {
  @apply mb-2;
}

:deep(.sanitized-html a) {
  @apply underline;
}

:deep(.sanitized-html a[target='_blank']::after) {
  @apply font-dietplus-runes content-['\e92b'] inline-block mx-1 text-[#ad948b] text-[0.8rem];
}
</style>
